// Normalize thrown values into a display string. Supabase PostgrestErrors are
// plain objects (not Error instances), so String(err) yields "[object Object]".
export function formatError(err: unknown, fallback = "Something went wrong."): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  if (typeof err === "object" && err && "message" in err) {
    const msg = (err as { message: unknown }).message;
    if (typeof msg === "string" && msg) return msg;
  }
  return fallback;
}

// SPEC manna-web-ui §3.13 — RLS-silent denials. Use this in any user-facing
// surface that might receive a permission-denied error so we never reveal
// that a record exists but is gated by the user's role.
//
// Scrubs Postgres / PostgREST error strings that mention permission,
// privilege, RLS, or the visibility-of-role copy and replaces them with a
// neutral fallback. Engineers can still see the original via console /
// Sentry — only the rendered string is sanitized.
const RLS_DENIAL_PATTERNS = [
  /permission denied/i,
  /insufficient[_\s]privilege/i,
  /not visible to your role/i,
  /you don't have (access|permission)/i,
  /rls/i,
  /\b42501\b/, // Postgres permission_denied SQLSTATE
];

export function isRlsDenial(err: unknown): boolean {
  const msg = formatError(err, "");
  if (!msg) return false;
  return RLS_DENIAL_PATTERNS.some((re) => re.test(msg));
}

export function formatErrorUserSafe(err: unknown, fallback = "Something went wrong."): string {
  if (isRlsDenial(err)) {
    if (typeof console !== "undefined") {
      console.warn("[rls-silent] suppressed denial message", err);
    }
    return fallback;
  }
  return formatError(err, fallback);
}

// Pulls the Postgres/PostgREST SQLSTATE off a thrown value, if present. Supabase
// surfaces it as `error.code` on the plain error object.
export function errorCode(err: unknown): string {
  if (typeof err === "object" && err && "code" in err) {
    return String((err as { code: unknown }).code ?? "");
  }
  return "";
}

// Decodes a deal-write RPC failure into clear, action-prefixed copy. Supabase
// errors are plain objects, so the old `String(err)` produced "[object Object]"
// (2026-06-03 BUG #2/#4). Maps the §8.4.7 SQLSTATE contract:
//   42501            → permission (the §8.4.6 role gate denied the write)
//   STALE / CONFLICT → optimistic-concurrency row_version mismatch
// Everything else falls back to "<action>: <real message>".
export function formatWriteError(err: unknown, actionPrefix: string): string {
  const code = errorCode(err);
  const msg = formatError(err, "");
  if (code === "42501") {
    return "You don't have permission to make this change.";
  }
  if (code === "STALE" || code === "CONFLICT" || /\bCONFLICT\b/.test(msg)) {
    return "Someone else updated this deal. Please refresh and try again.";
  }
  return `${actionPrefix}: ${formatError(err, "Something went wrong.")}`;
}
